import FooterNav from '@xo-union/tk-component-footer-nav/remote';
import { FullBleedContainer, TopLevelContainer, Row, Column } from '@xo-union/tk-component-grid';
import Layout from '@xo-union/tk-component-layout';
import React, { useContext, useState, useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { ThemesQuery } from '../generated/graphql';
import HeaderNav from './HeaderNav';
import analyticsPageData from './helpers/analyticsPageData';
import { getFirstOrNull } from './helpers/ThemeContext';
import TrackingContext from './helpers/TrackingContext';
import ThemeFiltersVariantContext, { ThemeFilters } from './helpers/ThemeFiltersContext';
import useThemes from './helpers/useThemes';
import Loader from './Loader';
import styles from './themePicker.scss';
import { ThemeGrid } from './Themes';
import { NoFilterResults } from './components/Filters/NoResults';
import { ThemesFilterHeader } from './Themes/ThemesFilterHeader';

const product = process.env.REACT_APP_ANALYTICS_PRODUCT;
const analyticsProps = { product };

type Themes = ThemesQuery['familyThemes'];

// matches familyThemes with filters
const filterFamilyTheme = (familyTheme: Themes[0], themeFilters: ThemeFilters | undefined) =>
  familyTheme.themes?.filter(
    theme =>
      (themeFilters?.colors?.some((option: string) => theme.categoryData?.colors.includes(option)) ?? true) &&
      (themeFilters?.seasons?.some((option: string) => theme.categoryData?.seasons.includes(option)) ?? true) &&
      (themeFilters?.styles?.some((option: string) => theme.categoryData?.styles.includes(option)) ?? true)
  );

// excludes familyThemes that don't match filters
// this allows the theme grid to display the correct family theme that matches the filters
// because theme grid by default only picks the first family theme and ignores the rest
// if no filters are set it simply returns all queries themes, note that theme grid only displays the first family theme
const filterThemes = (themes: Themes, themeFilters: ThemeFilters | undefined) =>
  themeFilters?.colors?.length || themeFilters?.styles?.length || themeFilters?.seasons?.length
    ? themes.reduce((filtered: Themes, familyTheme) => {
        const matchingFamilyThemes = filterFamilyTheme(familyTheme, themeFilters);
        if (matchingFamilyThemes?.length) {
          filtered.push({
            ...familyTheme,
            themes: matchingFamilyThemes,
          });
        }
        return filtered;
      }, [])
    : themes;

const ThemePicker = () => {
  const history = useHistory();
  const { themes, loading: themesLoading, sortOrder } = useThemes();
  const { themeFilters } = useContext(ThemeFiltersVariantContext);
  const { track } = useContext(TrackingContext);

  //@ts-ignore
  const [filteredThemes, setFilteredThemes] = useState<Themes>(() => filterThemes(themes, themeFilters));

  const [loading, setLoading] = useState<boolean>(true);
  const loadingTimerRef = useRef<NodeJS.Timeout>();

  const isFiltersSet = useMemo(
    () => Boolean(themeFilters?.styles?.length || themeFilters?.colors?.length || themeFilters?.seasons?.length),
    [themeFilters]
  );

  useEffect(() => {
    return history.listen(() => {
      const { category, name, ...rest } = analyticsPageData();
      window?.analytics?.page(category, name, rest);
    });
  }, [history]);

  useEffect(() => {
    if (!themesLoading && themes.length) {
      //@ts-ignore
      setFilteredThemes(() => filterThemes(themes, themeFilters));
      setLoading(false);
      track('Create Theme Grid Impression', {
        themeIdOrder: themes.map(t => getFirstOrNull(t.themes)?.id),
        themeOrder: themes.map(t => getFirstOrNull(t.themes)?.name),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themesLoading, themes]);

  useEffect(() => {
    // change filteredThemes when user sets filter
    //@ts-ignore
    if (themeFilters) setFilteredThemes(() => filterThemes(themes, themeFilters));

    // if loading is set elsewhere then we're done, exit out.
    if (themesLoading || loading) return;

    // if user changes filters we want to set loading
    // all themes are loaded in context and filtering them is instant
    // to avoid seeing a flicker on screen we need a longer loading time
    // so we set a half second loadingTimer
    setLoading(true);
    loadingTimerRef.current = setTimeout(() => setLoading(false), 500);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeFilters]);

  // clear loadingTimer when unmounting component
  useEffect(() => {
    return () => {
      if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout
      header={
        <>
          <HeaderNav product={product} />
        </>
      }
      footer={<FooterNav analyticsProps={analyticsProps} />}
    >
      <FullBleedContainer>
        <TopLevelContainer>
          <Row>
            <Column xs="12">
              <ThemesFilterHeader />
              {loading ? (
                <div className={styles.loaderWrap}>
                  <Loader />
                </div>
              ) : (
                <>
                  {filteredThemes?.length ? (
                    <ThemeGrid themes={filteredThemes} sortOrder={sortOrder} />
                  ) : (
                    <>
                      {isFiltersSet ? <NoFilterResults /> : null}
                      {
                        //@ts-ignore
                        <ThemeGrid themes={themes} sortOrder={sortOrder} />
                      }
                    </>
                  )}
                </>
              )}
            </Column>
          </Row>
        </TopLevelContainer>
      </FullBleedContainer>
    </Layout>
  );
};

export default ThemePicker;
